import React, { useEffect, useState, useCallback } from 'react';
import { CurrencyDollar, CheckCircle, ArrowSquareOut, Warning, LinkBreak } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Square Connect section for the producer Dashboard.
 *
 * Mirrors the legacy StripeConnectSection UX:
 *  - "Connect with Square" CTA when not connected (orange banner = pay attention)
 *  - "Connected" badge + Disconnect when active
 *  - Toast on success/error from the OAuth callback redirect (?square_oauth=...)
 *
 * When connected, all NEW shows the producer creates auto-route payments to
 * their account; existing pre-connection shows continue routing to the master
 * account until manually re-routed.
 */
export const SquareConnectSection = () => {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [routing, setRouting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusRes, summaryRes] = await Promise.all([
        api.get('/connect/square/status'),
        api.get('/connect/square/routing-summary').catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setSummary(summaryRes.data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Surface the result of the OAuth round-trip if we just came back.
    const params = new URLSearchParams(window.location.search);
    const result = params.get('square_oauth');
    if (result === 'success') {
      toast.success('Square account connected!');
      // Strip the query param so a refresh doesn't re-fire the toast
      window.history.replaceState({}, '', window.location.pathname);
    } else if (result === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Square connect failed: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/connect/square/oauth/start', {
        params: { return_to: '/dashboard' }
      });
      if (data?.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        toast.error('Failed to start Square OAuth');
        setConnecting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Square OAuth');
      setConnecting(false);
    }
  };

  const handleRouteAll = async () => {
    if (!summary?.eligible_master_shows) return;
    if (!window.confirm(`Re-route ${summary.eligible_master_shows} existing show(s) to your Square account? Future rider payments on these shows will deposit directly into your Square account.`)) return;
    setRouting(true);
    try {
      const { data } = await api.post('/connect/square/route-all-existing');
      toast.success(`Re-routed ${data.shows_updated || 0} show(s) to your Square account`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-route failed');
    } finally {
      setRouting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Square account? New shows will go back to routing payments to The Show Master master account.')) return;
    try {
      await api.post('/connect/square/disconnect');
      toast.success('Square disconnected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    }
  };

  if (loading) return <div className="mb-8 bg-[#111] border border-[#1F1F1F] rounded-2xl p-6 shimmer h-32" />;

  // -------- Connected --------
  if (status?.connected) {
    return (
      <div className="mb-8 bg-[#111] border border-emerald-500/30 rounded-2xl p-6 sm:p-8" data-testid="square-connect-section">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
            <CheckCircle size={22} weight="fill" className="text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Square Connected</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Active</span>
            </div>
            <p className="text-sm text-[#9E9E9E] mt-1">
              Payments from <strong>new shows</strong> you create now route directly to your Square account.
              The Show Master keeps just the service fee as the platform commission.
            </p>
            {status.merchant_id && (
              <p className="text-xs text-[#6B6B6B] mt-2 font-mono">Merchant: {status.merchant_id}</p>
            )}
          </div>
          <button
            onClick={handleDisconnect}
            className="flex items-center gap-1.5 text-xs text-[#9E9E9E] hover:text-red-400 transition-colors px-3 py-1.5 rounded border border-[#262626] hover:border-red-400/50"
            data-testid="square-disconnect-btn"
          >
            <LinkBreak size={14} /> Disconnect
          </button>
        </div>

        {/* Bulk re-route banner: shown when the producer has PUBLISHED/LIVE
            shows still going to the master account. v131 admin tool. */}
        {summary && summary.eligible_master_shows > 0 && (
          <div className="mt-5 bg-[#FF9500]/10 border border-[#FF9500]/40 rounded-lg p-4 flex items-start gap-3" data-testid="square-route-existing-banner">
            <Warning size={20} weight="duotone" className="text-[#FF9500] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-white font-semibold">
                {summary.eligible_master_shows} existing show{summary.eligible_master_shows === 1 ? '' : 's'} still routing to the master account
              </p>
              <p className="text-xs text-[#9E9E9E] mt-1">
                These were created before you connected Square. Re-route them and all future rider payments will go to your account instead.
              </p>
            </div>
            <button
              onClick={handleRouteAll}
              disabled={routing}
              className="bg-[#FF9500] hover:bg-[#FFA500] disabled:opacity-60 text-black font-bold text-xs px-4 py-2 rounded transition-colors flex-shrink-0"
              data-testid="square-route-all-btn"
            >
              {routing ? 'Re-routing…' : `Route ${summary.eligible_master_shows} show${summary.eligible_master_shows === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // -------- Not connected --------
  return (
    <div className="mb-8 bg-gradient-to-br from-[#FF9500]/10 to-[#0096FF]/5 border border-[#FF9500]/30 rounded-2xl p-6 sm:p-8" data-testid="square-connect-section">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#FF9500]/20 flex items-center justify-center flex-shrink-0">
          <Warning size={22} weight="duotone" className="text-[#FF9500]" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Connect your Square account
          </h2>
          <p className="text-sm text-[#E5E5E5] mt-2 max-w-2xl">
            Payments for your shows are currently routing to the <strong>Show Master master account</strong>.
            Connect your own Square account and money from new shows will deposit directly into your bank — the
            platform only takes a service fee per transaction.
          </p>
          <ul className="text-xs text-[#9E9E9E] mt-3 space-y-1">
            <li>• Instant deposits to your Square account (no manual payout from the master)</li>
            <li>• You manage your own refunds in your Square dashboard</li>
            <li>• Service fees still funnel to the platform automatically</li>
          </ul>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="mt-5 inline-flex items-center gap-2 bg-[#000] hover:bg-[#1A1A1A] disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors border border-white/10"
            data-testid="square-connect-btn"
          >
            <CurrencyDollar size={18} weight="bold" /> {connecting ? 'Connecting…' : 'Connect with Square'}
            <ArrowSquareOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
